import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Calendar, Users, MessageSquare, Star } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    appointments: 0,
    pendingTestimonials: 0,
    unreadMessages: 0
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [appointmentsRes, testimonialsRes, messagesRes] = await Promise.all([
        axios.get(`${API}/appointments`, { headers }),
        axios.get(`${API}/testimonials/pending`, { headers }),
        axios.get(`${API}/contact`, { headers })
      ]);

      setStats({
        appointments: appointmentsRes.data.filter(a => a.status !== 'cancelled').length,
        pendingTestimonials: testimonialsRes.data.length,
        unreadMessages: messagesRes.data.filter(m => !m.read).length
      });
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  const statCards = [
    { 
      label: 'Active Appointments', 
      value: stats.appointments, 
      icon: <Calendar className="w-6 h-6" />,
      color: 'bg-blue-500'
    },
    { 
      label: 'Pending Testimonials', 
      value: stats.pendingTestimonials, 
      icon: <Star className="w-6 h-6" />,
      color: 'bg-yellow-500'
    },
    { 
      label: 'Unread Messages', 
      value: stats.unreadMessages, 
      icon: <MessageSquare className="w-6 h-6" />,
      color: 'bg-green-500'
    }
  ];

  return (
    <AdminLayout title="Dashboard">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {statCards.map((stat, index) => (
          <div 
            key={index} 
            className="bg-white rounded-xl p-6 shadow-sm border border-slate-100"
            data-testid={`dashboard-stat-${index}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{stat.label}</p>
                <p className="text-3xl font-bold text-slate-800 mt-1">{stat.value}</p>
              </div>
              <div className={`w-12 h-12 rounded-full ${stat.color} flex items-center justify-center text-white`}>
                {stat.icon}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 bg-white rounded-xl p-6 shadow-sm border border-slate-100">
        <h2 className="text-xl font-semibold text-slate-800 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <a href="/admin/appointments" className="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
            <Calendar className="w-5 h-5 text-[#0e7490] mb-2" />
            <p className="font-medium text-slate-700">View Appointments</p>
          </a>
          <a href="/admin/blocked-slots" className="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
            <Users className="w-5 h-5 text-[#0e7490] mb-2" />
            <p className="font-medium text-slate-700">Manage Blocked Slots</p>
          </a>
          <a href="/admin/testimonials" className="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
            <Star className="w-5 h-5 text-[#0e7490] mb-2" />
            <p className="font-medium text-slate-700">Review Testimonials</p>
          </a>
          <a href="/admin/messages" className="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
            <MessageSquare className="w-5 h-5 text-[#0e7490] mb-2" />
            <p className="font-medium text-slate-700">View Messages</p>
          </a>
        </div>
      </div>
    </AdminLayout>
  );
}
